import { QueryClient } from "@tanstack/react-query";
import { BaseQueryKey } from "../base-query-key.service";
import {
    QueryKeyInvalidatorAfterPage,
    QueryKeyInvalidatorAfterLocale,
    QueryKeyInvalidatorAfterGroup,
    QueryKeyInvalidatorAfterScopes,
    QueryKeyInvalidatorAfterId,
    QueryKeyInvalidatorAfterArgs,
} from "./actors.type";

/**
 * Service class for invalidating queries using the fluent builder pattern.
 *
 * This class extends BaseQueryKey to provide query invalidation functionality.
 * It uses the fluent builder pattern to construct query keys for invalidating cached queries.
 * The order is enforced: page > locale > group > scopes > id > args.
 * ! IMPORTANT: The values passed to the invalidator should be consistent to avoid breaking the query cache.
 *
 * @example
 * const invalidator = new QueryKeyInvalidator(queryClient);
 * invalidator
 *   .page('/home')
 *   .locale('en')
 *   .group('user')
 *   .scopes(['profile', 'settings'])
 *   .id(123)
 *   .args({ filter: 'active' })
 *   .execute();
 */
class QueryKeyInvalidator extends BaseQueryKey<
    QueryKeyInvalidatorAfterPage,
    QueryKeyInvalidatorAfterLocale,
    QueryKeyInvalidatorAfterGroup,
    QueryKeyInvalidatorAfterScopes,
    QueryKeyInvalidatorAfterId,
    QueryKeyInvalidatorAfterArgs
> {
    private _queryClient: QueryClient;

    /**
     * Constructor for QueryKeyInvalidator.
     *
     * @param {QueryClient} queryClient - The query client instance from @tanstack/react-query.
     */
    constructor(queryClient: QueryClient) {
        super();
        this._queryClient = queryClient;
    }

    /**
     * Implementation of abstract method to create the fluent interface after page is set.
     *
     * @returns {QueryKeyInvalidatorAfterPage} - Returns interface for next available methods.
     */
    protected createAfterPageInterface(): QueryKeyInvalidatorAfterPage {
        return {
            locale: (locale: string) => this.locale(locale),
            group: (group: string) => this.group(group),
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after locale is set.
     *
     * @returns {QueryKeyInvalidatorAfterLocale} - Returns interface for next available methods.
     */
    protected createAfterLocaleInterface(): QueryKeyInvalidatorAfterLocale {
        return {
            group: (group: string) => this.group(group),
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after group is set.
     *
     * @returns {QueryKeyInvalidatorAfterGroup} - Returns interface for next available methods.
     */
    protected createAfterGroupInterface(): QueryKeyInvalidatorAfterGroup {
        return {
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after scopes is set.
     *
     * @returns {QueryKeyInvalidatorAfterScopes} - Returns interface for next available methods.
     */
    protected createAfterScopesInterface(): QueryKeyInvalidatorAfterScopes {
        return {
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after id is set.
     *
     * @returns {QueryKeyInvalidatorAfterId} - Returns interface for next available methods.
     */
    protected createAfterIdInterface(): QueryKeyInvalidatorAfterId {
        return {
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after args is set.
     *
     * @returns {QueryKeyInvalidatorAfterArgs} - Returns interface for next available methods.
     */
    protected createAfterArgsInterface(): QueryKeyInvalidatorAfterArgs {
        return {
            execute: () => this.execute(),
        };
    }

    /**
     * Execute the query invalidation.
     *
     * @returns {void}
     *
     * @example
     * const invalidator = new QueryKeyInvalidator(queryClient);
     * invalidator
     *   .page('/home')
     *   .locale('en')
     *   .group('user')
     *   .scopes(['profile', 'settings'])
     *   .id(123)
     *   .args({ filter: 'active' })
     *   .execute();
     * // This will invalidate all queries which starts with this queryKey:
     * [
     *   { page: '/home' },
     *   { language: 'en' },
     *   { group: 'user' },
     *   { scopes: ['profile', 'settings'] },
     *   { id: 123 },
     *   { optional: [{ filter: 'active' }] }
     * ]
     */
    public execute(): void {
        const queryKey = this.builder();
        this._queryClient.invalidateQueries({ queryKey });
    }
}

export { QueryKeyInvalidator };
export default QueryKeyInvalidator;
